import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cbrebuild/blocks';
import PercentDollarInput from '../../../nucleus/inputs/PercentDollarInput';
import Select from '../../../nucleus/select/select';
import NumberInput from '../../../nucleus/inputs/NumberInput';
import Currency from '../../../nucleus/formats/currency';
import { deleteConsideration, updateConsideration, updateConsiderationInState } from '../../../redux/actions/considerations-actions';
import { toast } from '../../../redux/actions/toasts-actions';
import { updateVoucher } from '../../../redux/actions/voucher/voucher-actions';
import calculatePercentValueFromTotal from '../../../utils/calculate-percent-value-from-total';
import useFormState, { EVENT_VALUE_TYPES, parseEventValue } from '../../../hooks/useFormState';
import { useReviewRowEditReporter } from '../ReviewTab/ReviewEditTrackingContext';

const disabledTypeMap = {
  'Free Monthly Rent': true,
  'Free Annual Rent': true,
};

const typeOverrides = {
  total_units: EVENT_VALUE_TYPES.FLOAT,
  amount: EVENT_VALUE_TYPES.FLOAT,
  commission_percent: EVENT_VALUE_TYPES.FLOAT,
  commission_amount: EVENT_VALUE_TYPES.FLOAT,
  considerations_is_percent: EVENT_VALUE_TYPES.BOOLEAN,
};

const selectConsiderationById = (state, id) => (
  state.voucherConsiderations.considerations.find(consideration => consideration.id === id)
);

const ConsiderationItem = ({
  id,
  showValidation,
  onDeleteReorder,
}) => {
  const consideration = useSelector(state => selectConsiderationById(state, id)) || {};
  const {
    considerations_is_percent,
    deal_type,
    options = [],
    voucherId,
  } = useSelector(state => ({
    voucherId: state.voucher.voucher.id,
    considerations_is_percent: state.voucher.voucher.considerations_is_percent,
    deal_type: state.deal.deal.deal_type,
    options: state.voucher.options.leaseConsiderationTypeOptions || [],
  }));
  const dispatch = useDispatch();

  const considerationEntity = {
    amount: consideration.amount,
    commission_amount: consideration.commission_amount,
    commission_percent: consideration.commission_percent,
    consideration_amount: consideration.consideration_amount,
    consideration_type: consideration.consideration_type,
    total_units: consideration.total_units,
  };
  const latestValuesRef = useRef(considerationEntity);
  const [draftDerivedValues, setDraftDerivedValues] = useState({});
  // Review-page edit metrics: typed values are written to Redux on every keystroke, so the
  // value from before the first keystroke is remembered here to report as "previous".
  const reportEdit = useReviewRowEditReporter({ rowType: 'consideration', rowId: id });
  const preEditValuesRef = useRef({});

  useEffect(() => {
    latestValuesRef.current = {
      ...latestValuesRef.current,
      amount: considerationEntity.amount,
      commission_amount: considerationEntity.commission_amount,
      commission_percent: considerationEntity.commission_percent,
      consideration_amount: considerationEntity.consideration_amount,
      consideration_type: considerationEntity.consideration_type,
      total_units: considerationEntity.total_units,
    };
  }, [
    considerationEntity.amount,
    considerationEntity.commission_amount,
    considerationEntity.commission_percent,
    considerationEntity.consideration_amount,
    considerationEntity.consideration_type,
    considerationEntity.total_units,
  ]);

  const saveConsiderationUpdate = (update, current, patchCurrent = () => {}) => {
    let patch = {};
    const getLatestValue = key => (
      Object.prototype.hasOwnProperty.call(latestValuesRef.current, key)
        ? latestValuesRef.current[key]
        : current?.[key]?.value
    );
    const latestCurrent = {
      amount: { value: getLatestValue('amount') },
      commission_amount: { value: getLatestValue('commission_amount') },
      commission_percent: { value: getLatestValue('commission_percent') },
      consideration_amount: { value: getLatestValue('consideration_amount') },
      consideration_type: { value: getLatestValue('consideration_type') },
      total_units: { value: getLatestValue('total_units') },
    };
    if ('consideration_type' in update && disabledTypeMap[update.consideration_type]) {
      patch = {
        amount: 0, consideration_amount: 0, commission_percent: 0, commission_amount: 0,
      };
    } else if ('total_units' in update || 'amount' in update) {
      patch.consideration_amount = latestCurrent.amount.value * latestCurrent.total_units.value;
      const values = calculatePercentValueFromTotal(patch.consideration_amount, considerations_is_percent, latestCurrent.commission_percent.value);
      if (considerations_is_percent) {
        patch.commission_amount = values.value;
      } else {
        patch.commission_percent = values.percentage;
      }
    }
    patchCurrent(patch);
    dispatch(updateConsideration(id, { ...update, ...patch }, { updateLoadedFinancialsOnly: true }));
  };

  const {
    amount, commission_amount, commission_percent, consideration_amount, consideration_type, total_units,
    onStateChange, onStateChanged,
  } = useFormState({
    ...considerationEntity,
  }, saveConsiderationUpdate, typeOverrides, { skip: true });

  const buildCurrentValues = update => ({
    amount: { value: 'amount' in update ? update.amount : latestValuesRef.current.amount },
    commission_amount: { value: 'commission_amount' in update ? update.commission_amount : latestValuesRef.current.commission_amount },
    commission_percent: { value: 'commission_percent' in update ? update.commission_percent : latestValuesRef.current.commission_percent },
    consideration_amount: { value: 'consideration_amount' in update ? update.consideration_amount : latestValuesRef.current.consideration_amount },
    consideration_type: { value: 'consideration_type' in update ? update.consideration_type : latestValuesRef.current.consideration_type },
    total_units: { value: 'total_units' in update ? update.total_units : latestValuesRef.current.total_units },
  });

  const buildDerivedPatch = (update, current) => {
    if ('consideration_type' in update && disabledTypeMap[update.consideration_type]) {
      return {
        amount: 0, consideration_amount: 0, commission_percent: 0, commission_amount: 0,
      };
    }
    if ('total_units' in update || 'amount' in update) {
      const nextConsiderationAmount = current.amount.value * current.total_units.value;
      const values = calculatePercentValueFromTotal(nextConsiderationAmount, considerations_is_percent, current.commission_percent.value);
      return {
        consideration_amount: nextConsiderationAmount,
        ...(considerations_is_percent
          ? { commission_amount: values.value }
          : { commission_percent: values.percentage }),
      };
    }
    return {};
  };

  const buildBlurUpdate = ({ target: { name, value, type } }) => {
    if (typeof name === 'object') {
      return name.reduce((acc, n) => {
        acc[n] = parseEventValue(value[n], typeOverrides[n] || type);
        return acc;
      }, {});
    }
    return {
      [name]: parseEventValue(value, typeOverrides[name] || type),
    };
  };

  const handleStateChange = (event) => {
    const update = buildBlurUpdate(event);
    Object.keys(update).forEach((key) => {
      if (!(key in preEditValuesRef.current)) preEditValuesRef.current[key] = latestValuesRef.current[key];
    });
    const current = buildCurrentValues(update);
    const patch = buildDerivedPatch(update, current);
    latestValuesRef.current = {
      ...latestValuesRef.current,
      ...update,
      ...patch,
    };
    setDraftDerivedValues(previousDraft => ({ ...previousDraft, ...update, ...patch }));
    onStateChange(event);
    dispatch(updateConsiderationInState(id, { ...update, ...patch }));
  };

  const handleStateChanged = (event) => {
    const update = buildBlurUpdate(event);
    Object.keys(update).forEach((key) => {
      const previous = key in preEditValuesRef.current ? preEditValuesRef.current[key] : latestValuesRef.current[key];
      delete preEditValuesRef.current[key];
      reportEdit(key, previous, update[key]);
    });
    const current = buildCurrentValues(update);
    const patch = buildDerivedPatch(update, current);
    latestValuesRef.current = {
      ...latestValuesRef.current,
      ...update,
      ...patch,
    };
    setDraftDerivedValues(previousDraft => ({ ...previousDraft, ...update, ...patch }));
    const hasChange = Object.keys(update).some(key => considerationEntity[key] !== update[key]);
    if (hasChange) {
      onStateChanged(event);
      return;
    }
    saveConsiderationUpdate(update, {
      amount: current.amount,
      commission_amount: current.commission_amount,
      commission_percent: current.commission_percent,
      consideration_amount: current.consideration_amount,
      consideration_type: current.consideration_type,
      total_units: current.total_units,
    });
  };

  const isDisabled = !!disabledTypeMap[consideration_type.value];

  const onDelete = async () => {
    try {
      await dispatch(deleteConsideration(id, { updateLoadedFinancialsOnly: true }));
      onDeleteReorder(id);
      dispatch(toast('Consideration row is successfully deleted.'));
    } catch (error) {
      dispatch(toast('Failed to delete the consideration. Please try again.'));
      console.error('Delete consideration error');
    }
  };

  const onUpdateVoucher = ({ target: { name, value } }) => {
    dispatch(updateVoucher(voucherId, { [name]: value }));
  };

  const invalidTotalUnit = (showValidation || total_units.dirty) && total_units.value === null;
  const invalidAmount = !isDisabled && (showValidation || amount.dirty) && amount.value === null;
  const isEditing = amount.editing || total_units.editing || commission_amount.editing || commission_percent.editing;
  const displayConsiderationAmount = isEditing && 'consideration_amount' in draftDerivedValues
    ? draftDerivedValues.consideration_amount
    : consideration_amount.value;
  const displayCommissionAmount = isEditing && 'commission_amount' in draftDerivedValues
    ? draftDerivedValues.commission_amount
    : commission_amount.value;
  const displayCommissionPercent = isEditing && 'commission_percent' in draftDerivedValues
    ? draftDerivedValues.commission_percent
    : commission_percent.value;

  useEffect(() => {
    if (!isEditing && Object.keys(draftDerivedValues).length) {
      setDraftDerivedValues({});
    }
  }, [draftDerivedValues, isEditing]);

  return (
    <div className="form-row-with-hints consideration-item-row">
      <div className="consideration-select-cell">
        {deal_type !== 'lease' ? 'Purchase Price' : (
          <Select
            defaultOption={consideration_type.value}
            onChange={({ value }) => handleStateChanged({
              target: { value, type: 'text', name: 'consideration_type' },
            })}
            options={options}
          />
        )}
      </div>
      <div className="consideration-period-quantity-cell">
        <NumberInput
          name="total_units"
          value={total_units.value}
          precision={4}
          integerPrecision={8}
          onChange={handleStateChange}
          onBlur={handleStateChanged}
          placeholder={consideration_type.value.includes('Monthly') ? 'Months' : 'Years'}
          invalid={invalidTotalUnit}
        />
        {invalidTotalUnit && (
          <p className="voucher-error-text">Required</p>
        )}
      </div>
      <div className="consideration-rent-price-cell">
        <NumberInput
          name="amount"
          disabled={isDisabled}
          value={amount.value}
          precision={2}
          onChange={handleStateChange}
          onBlur={handleStateChanged}
          placeholder="Amount"
          invalid={invalidAmount}
        />
        {invalidAmount && (
          <p className="voucher-error-text">Required</p>
        )}
      </div>
      <div className="consideration-total-cell">
        <Currency value={displayConsiderationAmount} emptyState="--" />
      </div>
      <div className="consideration-commission-cell">
        <PercentDollarInput
          name={{
            percentage: 'commission_percent',
            value: 'commission_amount',
            select: 'considerations_is_percent',
          }}
          onTypeChange={onUpdateVoucher}
          onChange={handleStateChange}
          onBlur={handleStateChanged}
          disabled={isDisabled}
          baseTotal={displayConsiderationAmount || 0}
          percentage={displayCommissionPercent}
          value={displayCommissionAmount}
          isPercent={considerations_is_percent}
        />
      </div>
      <div className="consideration-result-cell">
        <Currency value={displayCommissionAmount} emptyState="--" />
      </div>
      <div className="table-cell">
        <IconButton
          className="blxs-button-icon-small"
          iconName="close-circle"
          onClick={onDelete}
          variant="basic"
        />
      </div>
    </div>
  );
};

ConsiderationItem.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  showValidation: PropTypes.bool.isRequired,
  onDeleteReorder: PropTypes.func.isRequired,
};

export default ConsiderationItem;
